/**
 * Form dropdown options for Order Hub forms
 * These options are used across Order Management forms
 */

/**
 * Labels type for i18n support
 */
type Labels = Record<string, string> | undefined;

/**
 * i18n Configuration
 * List of constant variables that should be included for translation.
 * Brand names, product names, and identifiers should NOT be included.
 */
export const I18N_TRANSLATABLE_CONSTANTS = [
  'titleOptions',
  'customerTypeOptions',
  'registrationProcessOptions',
  'orderStatusOptions',
  'deliveryStatusOptions',
] as const;

export { getTitleOptions } from '../../constants/constants';

/**
 * Customer type options
 * @param labels - i18n labels from Contentful
 */
export const getCustomerTypeOptions = (labels?: Labels) => [
  { label: labels?.['const.customerTypeOptions.private'] ?? 'Private', value: 'Private' },
  { label: labels?.['const.customerTypeOptions.business'] ?? 'Business', value: 'Business' },
];

/**
 * Registration process options
 * @param labels - i18n labels from Contentful
 */
export const getRegistrationProcessOptions = (labels?: Labels) => [
  {
    label: labels?.['const.registrationProcessOptions.lucidRegistration'] ?? 'Lucid Registration',
    value: 'Lucid Registration',
  },
  {
    label: labels?.['const.registrationProcessOptions.selfRegistration'] ?? 'Self Registration',
    value: 'Self-Registration',
  },
];

/**
 * Order status options
 * @param labels - i18n labels from Contentful
 */
export const getOrderStatusOptions = (labels?: Labels) => [
  { label: labels?.['const.orderStatusOptions.pending'] ?? 'Pending', value: 'Pending' },
  { label: labels?.['const.orderStatusOptions.confirmed'] ?? 'Confirmed', value: 'Confirmed' },
  {
    label: labels?.['const.orderStatusOptions.inProduction'] ?? 'In Production',
    value: 'InProduction',
  },
  {
    label: labels?.['const.orderStatusOptions.readyForDelivery'] ?? 'Ready for Delivery',
    value: 'ReadyForDelivery',
  },
  { label: labels?.['const.orderStatusOptions.delivered'] ?? 'Delivered', value: 'Delivered' },
  { label: labels?.['const.orderStatusOptions.cancelled'] ?? 'Cancelled', value: 'Cancelled' },
];

/**
 * Delivery status options
 * @param labels - i18n labels from Contentful
 */
export const getDeliveryStatusOptions = (labels?: Labels) => [
  { label: labels?.['const.deliveryStatusOptions.scheduled'] ?? 'Scheduled', value: 'scheduled' },
  { label: labels?.['const.deliveryStatusOptions.inTransit'] ?? 'In Transit', value: 'in_transit' },
  { label: labels?.['const.deliveryStatusOptions.delivered'] ?? 'Delivered', value: 'delivered' },
  { label: labels?.['const.deliveryStatusOptions.delayed'] ?? 'Delayed', value: 'delayed' },
];

/**
 * Document category options
 * @param labels - i18n labels from Contentful
 */
export const getDocumentCategoryOptions = (labels?: Labels) => [
  {
    label: labels?.['const.documentCategoryOptions.driversLicense'] ?? "Driver's License",
    value: 'drivers_license',
  },
  {
    label: labels?.['const.documentCategoryOptions.proofOfAddress'] ?? 'Proof of Address',
    value: 'proof_of_address',
  },
  { label: labels?.['const.documentCategoryOptions.insurance'] ?? 'Insurance', value: 'insurance' },
  {
    label: labels?.['const.documentCategoryOptions.registration'] ?? 'Registration',
    value: 'registration',
  },
  { label: labels?.['const.documentCategoryOptions.contract'] ?? 'Contract', value: 'contract' },
  { label: labels?.['const.documentCategoryOptions.invoice'] ?? 'Invoice', value: 'invoice' },
];

/**
 * Payment method options
 * @param labels - i18n labels from Contentful
 */
export const getPaymentMethodOptions = (labels?: Labels) => [
  { label: labels?.['const.paymentMethodOptions.cash'] ?? 'Cash', value: 'cash' },
  { label: labels?.['const.paymentMethodOptions.lease'] ?? 'Lease', value: 'lease' },
  { label: labels?.['const.paymentMethodOptions.finance'] ?? 'Finance', value: 'finance' },
  {
    label: labels?.['const.paymentMethodOptions.creditCard'] ?? 'Credit Card',
    value: 'credit_card',
  },
  { label: labels?.['const.paymentMethodOptions.ach'] ?? 'ACH', value: 'ach' },
  {
    label: labels?.['const.paymentMethodOptions.cryptocurrency'] ?? 'Cryptocurrency',
    value: 'cryptocurrency',
  },
];

/**
 * Country code options for address fields
 * @param labels - i18n labels from Contentful
 */
export const getCountryOptions = (labels?: Labels) => [
  { label: labels?.['const.countryOptions.de'] ?? 'Germany (DE)', value: 'DE' },
  { label: labels?.['const.countryOptions.us'] ?? 'United States (US)', value: 'US' },
  { label: labels?.['const.countryOptions.ca'] ?? 'Canada (CA)', value: 'CA' },
  { label: labels?.['const.countryOptions.uk'] ?? 'United Kingdom (UK)', value: 'UK' },
  { label: labels?.['const.countryOptions.fr'] ?? 'France (FR)', value: 'FR' },
  { label: labels?.['const.countryOptions.it'] ?? 'Italy (IT)', value: 'IT' },
  { label: labels?.['const.countryOptions.es'] ?? 'Spain (ES)', value: 'ES' },
  { label: labels?.['const.countryOptions.nl'] ?? 'Netherlands (NL)', value: 'NL' },
  { label: labels?.['const.countryOptions.ch'] ?? 'Switzerland (CH)', value: 'CH' },
  { label: labels?.['const.countryOptions.at'] ?? 'Austria (AT)', value: 'AT' },
];
